import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UrlService } from './url.service';
import { QuestionnaireRowDto } from 'app/@types/dto/questionnaire-row-dto.model';
import { BatchUpdateRequestDto } from 'app/@types/dto/batch-update-request-dto.model';
import { CopyToMultipleDestinationsRequestDto } from 'app/@types/dto/copy-to-multiple-destinations-request-dto.model';
import { CopyToMultipleDestinationsResponseDto } from 'app/@types/dto/copy-to-multiple-destinations-response-dto.model';

@Injectable({
    providedIn: 'root',
})
// ------------------- Makes HTTP calls (GET, POST, PUT, DELETE) ------------------- //
export class AdvancedMeasureHttpService {
    constructor(
        private http: HttpClient,
        private urlService: UrlService,
    ) {}

    /**
     * Retrieves a specific advanced measure by its ID
     */
    get(organizationId: string, organizationEntityId: string, measureId: string): Observable<QuestionnaireRowDto> {
        return this.http.get<QuestionnaireRowDto>(
            this.urlService.getAdvancedMeasure(organizationId, organizationEntityId, measureId),
        );
    }

    /**
     *  Retrieves all advanced measures for a specific organization and entity and dimension
     */
    getAllByDimension(
        organizationId: string,
        organizationEntityId: string,
        params: Record<string, string | undefined>,
    ): Observable<QuestionnaireRowDto[]> {
        const url = this.http.get<QuestionnaireRowDto[]>(
            this.urlService.getAllAdvancedMeasuresByDimension(organizationId, organizationEntityId, params),
        );
        return url;
    }

    /**
     * Creates a new advanced measure
     */
    create(
        organizationId: string,
        organizationEntityId: string,
        measure: QuestionnaireRowDto,
    ): Observable<QuestionnaireRowDto> {
        const url = this.urlService.createAdvancedMeasure(organizationId, organizationEntityId);
        return this.http.post<QuestionnaireRowDto>(url, measure);
    }

    /**
     * Updates an existing advanced measure
     */
    update(
        organizationId: string,
        organizationEntityId: string,
        measure: QuestionnaireRowDto,
    ): Observable<QuestionnaireRowDto> {
        const url = this.urlService.updateAdvancedMeasure(organizationId, organizationEntityId);
        return this.http.put<QuestionnaireRowDto>(url, measure);
    }

    /**
     * Deletes an advanced measure
     */
    delete(organizationId: string, organizationEntityId: string, measureId: string): Observable<void> {
        return this.http.delete<void>(
            this.urlService.deleteAdvancedMeasure(organizationId, organizationEntityId, measureId),
        );
    }

    /**
     * Bulk deletes advanced measures
     */
    bulkDelete(
        organizationId: string,
        organizationEntityId: string,
        questionnaireId: string,
        measureIds: string[],
    ): Observable<void> {
        return this.http.post<void>(
            this.urlService.bulkDeleteAdvancedMeasures(organizationId, organizationEntityId, questionnaireId),
            measureIds,
        );
    }

    /**
     * Moves advanced measures
     */
    move(
        organizationId: string,
        organizationEntityId: string,
        destinationQuestionnaireID: string,
        measureIds: string[],
    ): Observable<void> {
        return this.http.put<void>(
            this.urlService.moveAdvancedMeasures(organizationId, organizationEntityId, destinationQuestionnaireID),
            measureIds,
        );
    }

    /**
     * Copies advanced measures from a source entity to multiple destination entities for one shared reporting year (best-effort).
     */
    copyMulti(
        organizationId: string,
        sourceOrganizationEntityId: string,
        payload: CopyToMultipleDestinationsRequestDto,
    ): Observable<CopyToMultipleDestinationsResponseDto> {
        return this.http.post<CopyToMultipleDestinationsResponseDto>(
            this.urlService.copyAdvancedMeasuresMulti(organizationId, sourceOrganizationEntityId),
            payload,
        );
    }

    /**
     * Bulk updates advanced measures
     */
    bulkUpdate(organizationId: string, organizationEntityId: string, request: BatchUpdateRequestDto): Observable<void> {
        return this.http.put<void>(
            this.urlService.bulkUpdateAdvancedMeasures(organizationId, organizationEntityId),
            request,
        );
    }
}
